import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { chatbotApi, adminApi } from "../../lib/api";
import {
  ArrowLeft, Sparkles, Save, Loader2, Plus, Trash2, Pencil, X,
  Check, AlertTriangle, TrendingUp, Ban, Zap, Clock, Cpu, Euro,
  MessageSquare, Hash, Bot, HelpCircle, Send, RotateCcw, MapPin, ExternalLink,
} from "lucide-react";

// -----------------------------------------------------------------------------
// AdminChatbot — /admin/chatbot
//
// Public label: "Assistente AI" (internal DB columns keep the `chatbot_*` prefix
// for backward compatibility). This component is portal-agnostic: all strings
// visible to the admin come from settings + assistant type presets.
// -----------------------------------------------------------------------------

const MODES = [
  { value: "disabled", label: "Disattivato", desc: "Il pulsante non compare pubblicamente. Nessuna chiamata al modello, costo zero." },
  { value: "preview", label: "Solo anteprima (admin)", desc: "Il widget è visibile solo se sei loggato come amministratore. Utile per testare prima di pubblicare." },
  { value: "public", label: "Attivo pubblicamente", desc: "Il widget è visibile a tutti i visitatori del portale." },
];

const ASSISTANT_TYPES = [
  { value: "ccn", label: "CCN", desc: "Centro Commerciale Naturale" },
  { value: "comune", label: "Comune", desc: "Portale istituzionale comunale" },
  { value: "proloco", label: "Pro Loco", desc: "Associazione turistico-culturale" },
  { value: "associazione", label: "Associazione", desc: "Ente non profit generico" },
  { value: "directory", label: "Directory", desc: "Elenco di attività o servizi" },
  { value: "personalizzato", label: "Personalizzato", desc: "Configurazione custom" },
];

const DATA_SOURCES = [
  { key: "chatbot_enable_businesses", label: "Attività", desc: "Ricerca su schede business (nome, servizi, tag, orari, contatti)", active: true },
  { key: "chatbot_enable_faqs", label: "FAQ istituzionali", desc: "Risposte curate gestite dall'admin", active: true },
  { key: "chatbot_enable_events", label: "Eventi", desc: "Attivabile quando la sezione eventi sarà pubblicata", active: false },
  { key: "chatbot_enable_news", label: "News", desc: "Predisposto per articoli e comunicati", active: false },
  { key: "chatbot_enable_institutional_pages", label: "Pagine istituzionali", desc: "Chi siamo, Statuto, Contatti", active: false },
  { key: "chatbot_enable_pdf_documents", label: "Documenti PDF", desc: "Predisposto per allegati e regolamenti", active: false },
];

const AdminChatbot = () => {
  const [settings, setSettings] = useState(null);
  const [initial, setInitial] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const [s, st] = await Promise.all([adminApi.getChatbotSettings(), adminApi.getChatbotStats()]);
      setSettings(s.data); setInitial(s.data); setStats(st.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento");
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));
  const dirty = settings && initial && JSON.stringify(settings) !== JSON.stringify(initial);

  const save = async () => {
    if (!dirty) return;
    setSaving(true); setError(null);
    try {
      const diff = {};
      Object.keys(settings).forEach(k => { if (settings[k] !== initial[k]) diff[k] = settings[k]; });
      const r = await adminApi.updateChatbotSettings(diff);
      setSettings(r.data); setInitial(r.data);
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-chatbot-page">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#12964A]" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Pannello
          </Link>
          <button
            onClick={save} disabled={!dirty || saving}
            data-testid="chatbot-save-btn"
            className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-40 text-white rounded-full px-5 py-2 text-sm"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvataggio…</> : <><Save className="w-4 h-4" /> {dirty ? "Salva modifiche" : "Salvato"}</>}
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 space-y-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#F0FAF3] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-7 h-7 text-[#12964A]" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Amministrazione</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Assistente AI</h1>
            <p className="text-slate-500 mt-2 max-w-2xl">
              Modulo Labix riutilizzabile — risponde soltanto usando i dati pubblicati sul portale.
              Kill-switch, modalità anteprima, sorgenti configurabili e limiti sotto il tuo controllo.
            </p>
          </div>
        </div>

        {savedAt && !dirty && (
          <div className="rounded-2xl bg-[#F0FAF3] border border-[#E1F3E8] px-4 py-2.5 text-sm text-[#0E7C3D] inline-flex items-center gap-2">
            <Check className="w-4 h-4" /> Impostazioni salvate — cache aggiornata immediatamente
          </div>
        )}
        {error && (
          <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700 inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* STATUS SUMMARY */}
        <StatusPanel stats={stats} settings={settings} />

        {/* KILL SWITCH */}
        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8" data-testid="mode-section">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Kill switch</div>
          <div className="font-display font-bold text-2xl text-[#1F2937] mb-4">Stato dell&apos;assistente</div>
          <div className="grid gap-3">
            {MODES.map((m) => (
              <label
                key={m.value}
                data-testid={`mode-${m.value}`}
                className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-colors ${
                  settings.chatbot_mode === m.value
                    ? "border-[#12964A] bg-[#F0FAF3]"
                    : "border-[#E5E7EB] hover:border-slate-300"
                }`}
              >
                <input
                  type="radio" name="chatbot_mode" value={m.value}
                  checked={settings.chatbot_mode === m.value}
                  onChange={() => patch("chatbot_mode", m.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-[#1F2937]">{m.label}</div>
                  <div className="text-sm text-slate-500 mt-0.5">{m.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* ASSISTANT TYPE */}
        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8" data-testid="assistant-type-section">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Tipo assistente</div>
          <div className="font-display font-bold text-2xl text-[#1F2937] mb-1">Contesto del portale</div>
          <p className="text-sm text-slate-500 mb-5">Predisposizione per adattare il tono e i suggerimenti a diverse tipologie di portale. Non modifica la logica corrente — utile per riutilizzi futuri.</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
            {ASSISTANT_TYPES.map((t) => (
              <label
                key={t.value}
                data-testid={`assistant-type-${t.value}`}
                className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition-colors ${
                  settings.chatbot_assistant_type === t.value
                    ? "border-[#12964A] bg-[#F0FAF3]"
                    : "border-[#E5E7EB] hover:border-slate-300"
                }`}
              >
                <input
                  type="radio" name="chatbot_assistant_type" value={t.value}
                  checked={settings.chatbot_assistant_type === t.value}
                  onChange={() => patch("chatbot_assistant_type", t.value)}
                  className="mt-1"
                />
                <div className="min-w-0">
                  <div className="font-medium text-[#1F2937] text-sm">{t.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* DATA SOURCES */}
        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8" data-testid="data-sources-section">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Sorgenti dati</div>
          <div className="font-display font-bold text-2xl text-[#1F2937] mb-1">Da dove pescare le risposte</div>
          <p className="text-sm text-slate-500 mb-5">Ogni sorgente può essere abilitata o disabilitata singolarmente. Le sorgenti marcate <span className="text-slate-400 font-medium">predisposte</span> sono già presenti nell&apos;architettura ma verranno collegate al retrieval in una versione futura.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {DATA_SOURCES.map((src) => (
              <label
                key={src.key}
                data-testid={`source-${src.key}`}
                className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer ${
                  settings[src.key] ? "border-[#12964A] bg-[#F0FAF3]" : "border-[#E5E7EB] hover:border-slate-300"
                } ${!src.active ? "opacity-80" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={!!settings[src.key]}
                  onChange={(e) => patch(src.key, e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#12964A]"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-[#1F2937] text-sm">{src.label}</div>
                    {!src.active && (
                      <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Predisposto</span>
                    )}
                    {src.active && (
                      <span className="text-[10px] uppercase tracking-wider bg-[#F0FAF3] text-[#0E7C3D] rounded-full px-2 py-0.5">Attivo</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{src.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* TEXT CUSTOMIZATION */}
        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Personalizzazione testi</div>
          <div className="font-display font-bold text-2xl text-[#1F2937] mb-6">Interfaccia</div>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Etichetta pulsante" testid="field-button-label">
              <input type="text" value={settings.chatbot_button_label || ""} onChange={(e) => patch("chatbot_button_label", e.target.value)} className={inp} />
            </Field>
            <Field label="Placeholder input" testid="field-placeholder">
              <input type="text" value={settings.chatbot_placeholder || ""} onChange={(e) => patch("chatbot_placeholder", e.target.value)} className={inp} />
            </Field>
            <Field label="Messaggio di benvenuto" full testid="field-welcome">
              <textarea rows={2} value={settings.chatbot_welcome_message || ""} onChange={(e) => patch("chatbot_welcome_message", e.target.value)} className={inp} />
            </Field>
            <Field label="Messaggio di fallback (quando non trova risposta)" full testid="field-fallback">
              <textarea rows={2} value={settings.chatbot_fallback_message || ""} onChange={(e) => patch("chatbot_fallback_message", e.target.value)} className={inp} />
            </Field>
          </div>
        </section>

        {/* LIMITS */}
        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Limiti e controllo costi</div>
          <div className="font-display font-bold text-2xl text-[#1F2937] mb-6">Guardrail operativi</div>
          <div className="grid md:grid-cols-3 gap-6">
            <Field label="Massimo risultati per risposta" testid="field-max-results">
              <input type="number" min={1} max={10} value={settings.chatbot_max_results} onChange={(e) => patch("chatbot_max_results", parseInt(e.target.value || "5", 10))} className={inp} />
            </Field>
            <Field label="Limite richieste giornaliere globali" testid="field-daily-cap">
              <input type="number" min={0} value={settings.chatbot_daily_cap} onChange={(e) => patch("chatbot_daily_cap", parseInt(e.target.value || "0", 10))} className={inp} />
              <div className="text-xs text-slate-400 mt-1">0 = illimitato (sconsigliato)</div>
            </Field>
            <Field label="Limite richieste per sessione" testid="field-session-cap">
              <input type="number" min={0} value={settings.chatbot_session_cap} onChange={(e) => patch("chatbot_session_cap", parseInt(e.target.value || "0", 10))} className={inp} />
            </Field>
          </div>
        </section>

        {/* FAQ CRUD */}
        <FaqManager />

        {/* Suggestions CRUD (V8) */}
        <SuggestionsManager />

        {/* Insights (V8) */}
        <InsightsPanel stats={stats} />

        {/* Test playground (V8.1) — admin-only inline chat */}
        <TestPlayground />
      </main>
    </div>
  );
};

const inp = "w-full rounded-xl border border-[#E5E7EB] focus:border-[#12964A] focus:ring-2 focus:ring-[#F0FAF3] outline-none px-4 py-2.5 text-sm";

const Field = ({ label, children, full, testid }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`} data-testid={testid}>
    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-2">{label}</div>
    {children}
  </label>
);

// ----- Status summary -------------------------------------------------------
const StatusDot = ({ mode }) => {
  const map = {
    public: { color: "bg-emerald-500", label: "Attivo" },
    preview: { color: "bg-amber-500", label: "Solo Anteprima" },
    disabled: { color: "bg-slate-400", label: "Disattivato" },
  };
  const m = map[mode] || map.disabled;
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`w-2.5 h-2.5 rounded-full ${m.color}`} />
      <span className="font-medium text-[#1F2937]">{m.label}</span>
    </span>
  );
};

const StatusPanel = ({ stats, settings }) => {
  if (!stats) return null;
  const remaining = stats.remaining_today < 0 ? "∞" : stats.remaining_today;
  const lastQuery = stats.last_query_at ? new Date(stats.last_query_at).toLocaleString("it-IT") : "—";

  return (
    <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8" data-testid="status-section">
      <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Riepilogo stato</div>
      <div className="font-display font-bold text-2xl text-[#1F2937] mb-6">Assistente AI</div>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <SummaryRow label="Stato" value={<StatusDot mode={settings.chatbot_mode} />} testid="row-stato" />
        <SummaryRow label="Richieste oggi" value={stats.today_ok} testid="row-today" icon={<TrendingUp className="w-3.5 h-3.5" />} />
        <SummaryRow label="Richieste bloccate oggi" value={stats.today_blocked} testid="row-blocked" icon={<Ban className="w-3.5 h-3.5" />} />
        <SummaryRow label="Rimanenti oggi" value={remaining} testid="row-remaining" icon={<Zap className="w-3.5 h-3.5" />} />
        <SummaryRow label="Richieste mese" value={stats.month_ok} testid="row-month" icon={<TrendingUp className="w-3.5 h-3.5" />} />
        <SummaryRow label="Ultima richiesta" value={lastQuery} testid="row-last" icon={<Clock className="w-3.5 h-3.5" />} />
        <SummaryRow label="Modello utilizzato" value={stats.model || "—"} testid="row-model" icon={<Cpu className="w-3.5 h-3.5" />} />
        <SummaryRow label="Token in / out (mese)" value={`${stats.tokens_input_month.toLocaleString("it-IT")} / ${stats.tokens_output_month.toLocaleString("it-IT")}`} testid="row-tokens" />
        <SummaryRow label="Stima costo (mese)" value={`€ ${stats.estimated_cost_month_eur.toFixed(3)}`} testid="row-cost" icon={<Euro className="w-3.5 h-3.5" />} />
      </div>
    </section>
  );
};

const SummaryRow = ({ label, value, icon, testid }) => (
  <div className="rounded-2xl bg-slate-50 px-4 py-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 flex items-center gap-1.5">{icon}{label}</div>
    <div className="text-sm text-[#1F2937] font-medium mt-1 truncate">{value}</div>
  </div>
);

// ----- FAQ CRUD -------------------------------------------------------------
const FaqManager = () => {
  const [faqs, setFaqs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try { const r = await adminApi.listFaqs(); setFaqs(r.data || []); }
    catch (e) { setError(e?.response?.data?.detail || "Errore FAQ"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id && !data.new) await adminApi.updateFaq(data.id, data);
      else await adminApi.createFaq(data);
      setEditing(null);
      await load();
    } catch (e) { setError(e?.response?.data?.detail || "Errore salvataggio FAQ"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa FAQ?")) return;
    try { await adminApi.deleteFaq(id); await load(); }
    catch (e) { setError(e?.response?.data?.detail || "Errore eliminazione"); }
  };

  return (
    <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8" data-testid="faqs-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Base conoscenza</div>
          <div className="font-display font-bold text-2xl text-[#1F2937]">FAQ istituzionali</div>
          <p className="text-sm text-slate-500 mt-1">Risposte curate su temi non presenti nelle schede attività (presidente, statuto, come iscriversi…).</p>
        </div>
        <button
          onClick={() => setEditing({ new: true, question: "", answer: "", keywords: [], display_order: 0, published: true })}
          data-testid="faq-add-btn"
          className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-4 py-2 text-sm"
        >
          <Plus className="w-4 h-4" /> Nuova FAQ
        </button>
      </div>
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2 mb-3">{error}</div>}
      {faqs === null && <div className="text-slate-400 text-sm">Caricamento…</div>}
      {faqs && faqs.length === 0 && !editing && (
        <div className="text-slate-500 text-sm bg-slate-50 rounded-2xl p-6 text-center">Nessuna FAQ ancora. Applica <code>supabase_patch_v7.sql</code> e usa il pulsante sopra per aggiungerne.</div>
      )}
      <div className="space-y-2" data-testid="faqs-list">
        {faqs?.map((f) => (
          <div key={f.id} className="border border-[#E5E7EB] rounded-2xl px-4 py-3 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#1F2937]">{f.question}</div>
              <div className="text-sm text-slate-500 mt-0.5 line-clamp-2">{f.answer}</div>
              {f.keywords?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {f.keywords.map((k, i) => <span key={i} className="text-[10px] bg-slate-100 rounded-full px-2 py-0.5 text-slate-600">{k}</span>)}
                </div>
              )}
            </div>
            <button onClick={() => setEditing(f)} className="text-slate-500 hover:text-[#12964A] p-1.5" aria-label="Modifica"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(f.id)} className="text-slate-500 hover:text-red-600 p-1.5" aria-label="Elimina"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {editing && <FaqEditor initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
    </section>
  );
};

const FaqEditor = ({ initial, onCancel, onSave }) => {
  const [q, setQ] = useState(initial.question || "");
  const [a, setA] = useState(initial.answer || "");
  const [kw, setKw] = useState((initial.keywords || []).join(", "));
  const [order, setOrder] = useState(initial.display_order || 0);
  const [pub, setPub] = useState(initial.published !== false);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-3xl w-full max-w-2xl p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="faq-editor">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-xl text-[#1F2937]">{initial.new ? "Nuova FAQ" : "Modifica FAQ"}</div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Domanda" full testid="faq-q">
            <input value={q} onChange={(e) => setQ(e.target.value)} className={inp} />
          </Field>
          <Field label="Risposta (supporta Markdown: [testo](/url))" full testid="faq-a">
            <textarea rows={5} value={a} onChange={(e) => setA(e.target.value)} className={inp} />
          </Field>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Parole chiave (separate da virgola)" full testid="faq-kw">
              <input value={kw} onChange={(e) => setKw(e.target.value)} className={inp} />
            </Field>
            <Field label="Ordine" testid="faq-order">
              <input type="number" value={order} onChange={(e) => setOrder(parseInt(e.target.value || "0", 10))} className={inp} />
            </Field>
          </div>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E7EB] cursor-pointer" data-testid="faq-pub">
            <input type="checkbox" checked={!!pub} onChange={(e) => setPub(e.target.checked)} className="w-4 h-4 accent-[#12964A]" />
            <span className="text-sm text-slate-700">Pubblicata (visibile all&apos;assistente)</span>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2">Annulla</button>
          <button
            onClick={() => onSave({
              ...initial,
              question: q.trim(), answer: a.trim(),
              keywords: kw.split(",").map(s => s.trim()).filter(Boolean),
              display_order: order, published: pub,
            })}
            disabled={!q.trim() || !a.trim()}
            data-testid="faq-save-btn"
            className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-40 text-white rounded-full px-5 py-2 text-sm"
          >
            <Save className="w-4 h-4" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminChatbot;

// ============================================================================
// V8 additions
// ============================================================================

// --- Suggestions CRUD -------------------------------------------------------
function SuggestionsManager() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try { const r = await adminApi.listSuggestions(); setItems(r.data || []); }
    catch (e) { setError(e?.response?.data?.detail || "Errore suggerimenti"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id && !data.new) await adminApi.updateSuggestion(data.id, data);
      else await adminApi.createSuggestion(data);
      setEditing(null);
      await load();
    } catch (e) { setError(e?.response?.data?.detail || "Errore salvataggio"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa domanda suggerita?")) return;
    try { await adminApi.deleteSuggestion(id); await load(); }
    catch (e) { setError(e?.response?.data?.detail || "Errore eliminazione"); }
  };

  return (
    <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8" data-testid="suggestions-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Domande suggerite</div>
          <div className="font-display font-bold text-2xl text-[#1F2937]">Chip iniziali</div>
          <p className="text-sm text-slate-500 mt-1">Mostrate quando l&apos;utente apre l&apos;assistente. Puoi personalizzarle per orientare le prime domande.</p>
        </div>
        <button
          onClick={() => setEditing({ new: true, text: "", display_order: 0, published: true })}
          data-testid="suggestion-add-btn"
          className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-4 py-2 text-sm"
        >
          <Plus className="w-4 h-4" /> Nuova domanda
        </button>
      </div>
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2 mb-3">{error}</div>}
      {items === null && <div className="text-slate-400 text-sm">Caricamento…</div>}
      {items && items.length === 0 && !editing && (
        <div className="text-slate-500 text-sm bg-slate-50 rounded-2xl p-6 text-center">Nessuna domanda suggerita. Applica <code>supabase_patch_v8.sql</code> e aggiungine dal pulsante sopra.</div>
      )}
      <div className="space-y-2" data-testid="suggestions-list">
        {items?.map((s) => (
          <div key={s.id} className="border border-[#E5E7EB] rounded-2xl px-4 py-3 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#1F2937]">{s.text}</div>
              <div className="text-xs text-slate-400 mt-0.5">Ordine: {s.display_order} · {s.published ? "Pubblicata" : "Nascosta"}</div>
            </div>
            <button onClick={() => setEditing(s)} className="text-slate-500 hover:text-[#12964A] p-1.5" aria-label="Modifica"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(s.id)} className="text-slate-500 hover:text-red-600 p-1.5" aria-label="Elimina"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {editing && <SuggestionEditor initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
    </section>
  );
}

function SuggestionEditor({ initial, onCancel, onSave }) {
  const [text, setText] = useState(initial.text || "");
  const [order, setOrder] = useState(initial.display_order || 0);
  const [pub, setPub] = useState(initial.published !== false);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="suggestion-editor">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-xl text-[#1F2937]">{initial.new ? "Nuova domanda" : "Modifica domanda"}</div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <label className="block">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-2">Testo (mostrato come chip)</div>
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} className={inp} data-testid="suggestion-text" />
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-2">Ordine di visualizzazione</div>
            <input type="number" value={order} onChange={(e) => setOrder(parseInt(e.target.value || "0", 10))} className={inp} />
          </label>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E7EB] cursor-pointer">
            <input type="checkbox" checked={!!pub} onChange={(e) => setPub(e.target.checked)} className="w-4 h-4 accent-[#12964A]" />
            <span className="text-sm text-slate-700">Pubblicata (visibile agli utenti)</span>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2">Annulla</button>
          <button
            onClick={() => onSave({ ...initial, text: text.trim(), display_order: order, published: pub })}
            disabled={!text.trim()}
            data-testid="suggestion-save-btn"
            className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-40 text-white rounded-full px-5 py-2 text-sm"
          >
            <Save className="w-4 h-4" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Insights (top keywords / businesses / faqs) ----------------------------
function InsightsPanel({ stats }) {
  if (!stats) return null;
  const kw = stats.top_keywords || [];
  const bz = stats.top_businesses || [];
  const fq = stats.top_faqs || [];
  const empty = kw.length === 0 && bz.length === 0 && fq.length === 0;

  return (
    <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8" data-testid="insights-section">
      <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Statistiche di utilizzo</div>
      <div className="font-display font-bold text-2xl text-[#1F2937] mb-1">Panoramica generale</div>
      <p className="text-sm text-slate-500 mb-6">Aggregati anonimi calcolati solo su interazioni con l&apos;assistente. Nessun dato personale.</p>

      {empty ? (
        <div className="text-slate-500 text-sm bg-slate-50 rounded-2xl p-6 text-center">Nessun dato di utilizzo ancora — apparirà qui appena l&apos;assistente verrà utilizzato dai visitatori.</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          <InsightList title="Parole più cercate" icon={<Hash className="w-4 h-4" />} items={kw.map(k => ({ label: k.keyword, count: k.count }))} testid="insight-keywords" />
          <InsightList title="Attività più suggerite" icon={<Bot className="w-4 h-4" />} items={bz.map(b => ({ label: b.name || b.business_id, count: b.count, href: b.slug ? `/attivita/${b.slug}` : null }))} testid="insight-businesses" />
          <InsightList title="FAQ più utilizzate" icon={<HelpCircle className="w-4 h-4" />} items={fq.map(f => ({ label: f.question || f.faq_id, count: f.count }))} testid="insight-faqs" />
        </div>
      )}
    </section>
  );
}

function InsightList({ title, icon, items, testid }) {
  return (
    <div className="border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid={testid}>
      <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-[0.15em] text-slate-600 font-medium flex items-center gap-2">
        {icon} {title}
      </div>
      <div className="divide-y divide-[#F1F5F9] max-h-[240px] overflow-y-auto">
        {items.length === 0 && <div className="px-4 py-3 text-xs text-slate-400">Nessun dato ancora</div>}
        {items.map((it, i) => (
          <div key={i} className="px-4 py-2 text-sm flex items-center gap-2">
            <span className="text-slate-400 text-xs w-5 flex-shrink-0">{i + 1}.</span>
            {it.href ? (
              <a href={it.href} className="text-[#12964A] hover:underline flex-1 truncate">{it.label}</a>
            ) : (
              <span className="text-slate-700 flex-1 truncate">{it.label}</span>
            )}
            <span className="text-xs text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 flex-shrink-0">{it.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Test Playground (V8.1) -------------------------------------------------
// Inline chat inside the admin panel for quickly testing the assistant
// without leaving the configuration page. Uses the same public endpoints
// as the widget (same session, same backend, same limits).
function TestPlayground() {
  const sessionId = useMemo(
    () => "admin_test_" + Math.random().toString(36).slice(2, 12),
    []
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    chatbotApi.suggestions().then(r => setSuggestions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (raw) => {
    const q = (raw || "").trim();
    if (!q || sending) return;
    setMessages(prev => [...prev, { role: "user", text: q }]);
    setInput(""); setSending(true);
    const history = messages.slice(-10).map(m => ({ role: m.role, text: m.text }));
    try {
      const r = await chatbotApi.ask(q, sessionId, history);
      setMessages(prev => [...prev, {
        role: "assistant",
        text: r.data.answer,
        links: r.data.links || [],
        blocks: r.data.blocks || [],
      }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore temporaneo.";
      setMessages(prev => [...prev, { role: "assistant", text: detail, error: true }]);
    } finally { setSending(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } };
  const reset = () => setMessages([]);

  return (
    <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8" data-testid="test-playground-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Test integrato</div>
          <div className="font-display font-bold text-2xl text-[#1F2937]">Prova l&apos;assistente</div>
          <p className="text-sm text-slate-500 mt-1">Chat di prova visibile solo a te. Usa le stesse impostazioni e sorgenti configurate qui sopra — utile per verificare modifiche prima di attivare pubblicamente.</p>
        </div>
        {messages.length > 0 && (
          <button
            onClick={reset}
            data-testid="playground-reset"
            className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-slate-400 rounded-full px-4 py-2 text-sm text-slate-600"
          >
            <RotateCcw className="w-4 h-4" /> Nuova conversazione
          </button>
        )}
      </div>

      <div className="border border-[#E5E7EB] rounded-2xl overflow-hidden bg-[#FAFBFC] flex flex-col" style={{ height: 480 }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6" data-testid="playground-empty">
              Scrivi una domanda qui sotto oppure prova uno dei suggerimenti.
            </div>
          )}
          {messages.map((m, i) => <PlaygroundMessage key={i} m={m} />)}
          {suggestions.length > 0 && messages.length === 0 && (
            <div className="flex flex-wrap gap-1.5 justify-center pt-2">
              {suggestions.map(s => (
                <button
                  key={s.id || s.text}
                  onClick={() => send(s.text)}
                  className="text-xs bg-white border border-[#E5E7EB] hover:border-[#12964A] hover:bg-[#F0FAF3] text-slate-700 rounded-full px-3 py-1.5"
                >
                  {s.text}
                </button>
              ))}
            </div>
          )}
          {sending && (
            <div className="flex items-center gap-2 text-slate-500 text-xs px-3">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sto cercando…
            </div>
          )}
        </div>
        <div className="border-t border-[#E5E7EB] bg-white p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Prova una domanda: 'Dove posso mangiare?'"
              rows={1}
              disabled={sending}
              data-testid="playground-input"
              className="flex-1 resize-none rounded-2xl border border-[#E5E7EB] focus:border-[#12964A] focus:ring-2 focus:ring-[#F0FAF3] outline-none px-4 py-2.5 text-sm max-h-24"
            />
            <button
              onClick={() => send(input)}
              disabled={sending || !input.trim()}
              data-testid="playground-send"
              aria-label="Invia"
              className="bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-40 text-white rounded-full p-2.5 flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="text-[10px] text-slate-400 mt-2 text-center">
            Ogni test consuma dal limite giornaliero configurato ({/* live-shown */}vedi statistiche sopra).
          </div>
        </div>
      </div>
    </section>
  );
}

function PlaygroundMessage({ m }) {
  const renderLinks = (text) => {
    const nodes = [];
    const re = /\[([^\]]{1,200})\]\((\/[^)\s]{0,200})\)/g;
    let last = 0; let match;
    while ((match = re.exec(text))) {
      if (match.index > last) nodes.push(text.slice(last, match.index));
      nodes.push(
        <a key={`l-${match.index}`} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-[#12964A] underline">
          {match[1]}
        </a>
      );
      last = match.index + match[0].length;
    }
    if (last < text.length) nodes.push(text.slice(last));
    return nodes;
  };

  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-[#12964A] text-white rounded-2xl rounded-tr-sm px-4 py-2 text-sm whitespace-pre-wrap">
          {m.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex">
      <div className={`max-w-[92%] rounded-2xl rounded-tl-sm px-4 py-2 text-sm ${m.error ? "bg-red-50 text-red-800 border border-red-100" : "bg-white text-[#1F2937] border border-[#E5E7EB]"}`}>
        <div className="whitespace-pre-wrap leading-relaxed">{renderLinks(m.text)}</div>
        {m.blocks?.filter(b => b.type === "business_card").length > 0 && (
          <div className="mt-3 grid gap-2">
            {m.blocks.filter(b => b.type === "business_card").map((b, i) => (
              <PlaygroundBusinessCard key={i} data={b.data || {}} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PlaygroundBusinessCard({ data }) {
  const { name, category, short_description, address, cover_image_url, url, maps_url } = data;
  if (!name || !url) return null;
  return (
    <div className="border border-[#E5E7EB] rounded-xl overflow-hidden bg-white flex">
      {cover_image_url && (
        <div className="w-20 h-20 bg-slate-100 flex-shrink-0">
          <img src={cover_image_url} alt={name} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
      )}
      <div className="p-2.5 flex-1 min-w-0">
        {category && <div className="text-[9px] uppercase tracking-[0.15em] text-[#12964A]">{category}</div>}
        <div className="font-semibold text-xs text-[#1F2937] truncate">{name}</div>
        {address && (
          <div className="text-[11px] text-slate-500 mt-0.5 truncate flex items-center gap-1">
            <MapPin className="w-2.5 h-2.5" /> {address}
          </div>
        )}
        <div className="flex gap-1.5 mt-1.5">
          <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-2 py-0.5">
            Apri <ExternalLink className="w-2.5 h-2.5" />
          </a>
          {maps_url && (
            <a href={maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] bg-white border border-[#E5E7EB] hover:border-[#12964A] text-slate-700 rounded-full px-2 py-0.5">
              Maps <MapPin className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
